import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";

import { PaneContextProvider, usePaneContext } from "../pane/context";

const ModalContext = createContext<{
  register: (id: string, content: React.ReactNode) => () => void;
  open: string | null;
  setOpen: (id: string | null) => void;
}>(new Proxy({} as never, { get: () => new Error("Missing Modal Provider") }));

export const ModalProvider = ({ children: propsChildren }: { children: React.ReactNode }) => {
  const [children, setChildren] = useState<Record<string, React.ReactNode>>({});
  const [open, setOpen] = useState<string | null>(null);

  const value = useMemo(() => {
    const register = (id: string, content: React.ReactNode) => {
      setChildren((prev) => ({ ...prev, [id]: content }));
      return () =>
        setChildren((prev) => {
          const newChildren = { ...prev };
          delete newChildren[id];
          return newChildren;
        });
    };
    return { register, open, setOpen };
  }, [open]);

  const child = open ? children[open] : null;

  return (
    <ModalContext.Provider value={value}>
      {child}
      {propsChildren}
    </ModalContext.Provider>
  );
};

// --- Modal Drag Constraint -------------------------------------------------
export const ModalDragConstraintContext = createContext<React.RefObject<HTMLDivElement>>({ current: null });

export const ModalDragConstraintProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }: { children: React.ReactNode }) => {
  const parentRef = useRef<HTMLDivElement>(null);
  return (
    <ModalDragConstraintContext.Provider value={parentRef}>
      <div className="w-full h-full relative" ref={parentRef}>
        {children}
      </div>
    </ModalDragConstraintContext.Provider>
  );
};

/**
 * Name the results of this hook `somethingRef`, because
 * React compiler gives this feedback from passing refs into hooks:
 *
 * Error: This value cannot be modified
 * Hint: If this value is a Ref (value returned by `useRef()`), rename the variable to end in "Ref".
 */
export const useModalDragConstraint = () => useContext(ModalDragConstraintContext);

export const useCloseModals = () => {
  const modal = useContext(ModalContext);
  return () => modal.setOpen(null);
};

/**
 * Hooks for rendering a modal. requires a ModalProvider to be in the tree.
 *
 * @example
 * const modal = useModal((props: { message: string }) => (
 *   <div>
 *     {props.message}
 *   </div>
 * ));
 *
 * modal.open({ message: "Hello" });
 * modal.close();
 */

// TODO: i think this is quite inefficient - Modal is often going to be a brand new closure, meaning the children memo is recomputed repeatedly
// and it's unclear that the React compiler will be able to recognise and optimise the function passed to useModal
// biome-ignore lint/complexity/noUselessTypeConstraint: Breaks syntax highlighting
export const useModal = <P extends any = void>(Modal: (p: P) => React.ReactNode) => {
  const [props, setProps] = useState<P | null>(null);
  const { open, register: modalRegister, setOpen: modalSetOpen } = useContext(ModalContext);
  const id = useId();
  const visible = open === id;
  const pane = usePaneContext();

  const children = useMemo(() => {
    if (!props) return null;
    return (
      <PaneContextProvider value={pane}>
        <Modal {...props} close={() => modalSetOpen(null)} />
      </PaneContextProvider>
    );
  }, [props, Modal, modalSetOpen, pane]);

  useEffect(() => modalRegister(id, children));
  useEffect(() => {
    modalSetOpen(props ? id : null);
  }, [props, modalSetOpen, id]);

  return useMemo(
    () => ({
      open: (props: P) => setProps((props ?? {}) as P),
      close: () => setProps(null),
      visible,
    }),
    [visible],
  );
};

// biome-ignore lint/complexity/noUselessTypeConstraint: Breaks syntax highlighting
export const useMemoModal = <P extends any = void>(Modal: (p: P) => React.ReactNode, deps: unknown[] = []) => {
  "use no memo"; // FIXME: REACT-COMPILER - Error: Expected the dependency list for useCallback to be an array literal

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to control deps manually
  return useModal(useCallback(Modal, deps));
};
